import { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import Table from "../../../components/table/table";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import { ClearAllOutlined } from "@mui/icons-material";
import TableHeaderTextField from "../../../components/table-header-text-filed/table-header-text-filed";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllOrdersRequest,
  getAllOrdersSuccess,
} from "../../../redux/orders/slice";
import dayjs from "dayjs";
import OrderTableFlagsCell from "../../../components/order-table/flags-cell";
import OrderTableOrderIdCell from "../../../components/order-table/orderId-cell";
import OrderTableRushCell from "../../../components/order-table/rush-cell";
import OrderTableClientCell from "../../../components/order-table/client-cell";
import OrderTableStatusCell from "../../../components/order-table/status-cell";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import TableHeaderSelectField from "../../../components/table-header-select-field/table-header-select-field";
import { queueType, userType } from "../../../utils/Constant";
import {
  deleteOrderRequest,
  updateQuickNoteRequest,
} from "../../../utils/APICalls";
import DeleteOrderDialog from "../../../components/delete-order-dialog/delete-order-dialog";
import Notification from "../../../components/notification/notification";
import ErrorNotification from "../../../components/error-notification/error-notification";
import QuickNoteEditCell from "../../../components/order-table/note-edit-cell";
import Loading from "../../../components/loading/loading";
import AdvancedDatePicker from "../../../components/advanced-date-picker/advanced-date-picker";

export default function AllCreated() {
  const dispatch = useDispatch();
  const [rowData, setRowData] = useState<any>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [filterParams, setFilterParams] = useState({
    queueType: queueType.created,
  });
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [filterResetKey, setFilterResetKey] = useState(0);

  const { loading, allOrders } = useSelector((state: any) => state.orders);
  const { currentUser } = useSelector((state: any) => state.user.user);

  const rowCountRef = useRef(allOrders?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (allOrders?.totalCount !== undefined) {
      rowCountRef.current = allOrders?.totalCount;
    }
    return rowCountRef.current;
  }, [allOrders?.totalCount]);

  const column: GridColDef[] = [
    {
      field: "flags",
      headerName: "Flags",
      width: 120,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableFlagsCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <Typography sx={{ fontWeight: "bold" }}>Flags</Typography>
      ),
    },
    {
      field: "orderNumber",
      headerName: "Order ID",
      width: 160,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableOrderIdCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="orderNumber"
          sortField="orderid"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "orderStatus",
      headerName: "Status",
      width: 160,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableStatusCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="orderStatus"
          sortField="status"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={["OrderAdded", "OrderReview", "OrderPending"]}
        />
      ),
    },
    {
      field: "isRushInspection",
      headerName: "Rush?",
      width: 110,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableRushCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="isRushInspection"
          sortField="isrushinspection"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={["Yes", "No"]}
        />
      ),
    },
    ...(currentUser?.userType === userType.tenant
      ? [
        {
          field: "quickNote",
          headerName: "Quick Note",
          width: 180,
          sortable: false,
          editable: true,
          resizable: false,
          headerClassName: "no-separator",
          renderEditCell: (params: any) => (
            <QuickNoteEditCell params={params} isEdit={true} />
          ),
          renderCell: (params: any) => (
            <QuickNoteEditCell params={params} isEdit={false} />
          ),
          renderHeader: (params: any) => (
            <TableHeaderTextField
              field="quickNote"
              sortField="quicknote"
              headerName={params.colDef.headerName}
              width={params.colDef.computedWidth}
              showFilter={showTableFilter}
              onFilterSubmit={onFilterUsers}
            />
          ),
        },
      ]
      : []),
    ...(currentUser?.userType !== userType.client
      ? [
        {
          field: "client",
          headerName: "Client",
          width: 180,
          sortable: false,
          resizable: false,
          headerClassName: "no-separator",
          renderCell: (params: GridRenderCellParams<any>) => (
            <OrderTableClientCell rowData={params.row} />
          ),
          renderHeader: (params: any) => (
            <TableHeaderTextField
              field="clientName"
              sortField="clientname"
              headerName={params.colDef.headerName}
              width={params.colDef.computedWidth}
              showFilter={showTableFilter}
              onFilterSubmit={onFilterUsers}
            />
          ),
        },
      ]
      : []),
    {
      field: "formName",
      headerName: "Inspection Name",
      width: 200,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderHeader: (params: any) => (
        <TableHeaderTextField
          field="formName"
          sortField="formname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "clientDueDate",
      headerName: "Client Due Date",
      width: 180,
      sortable: false,
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY") : " ",
      renderHeader: (params) => (
        <AdvancedDatePicker
          field="clientDueDate"
          sortField="clientduedate"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    ...(currentUser?.userType !== userType.client
      ? [
        {
          field: "eta",
          headerName: "ETA",
          width: 100,
          sortable: false,
          resizable: false,
          headerClassName: "no-separator",
          valueGetter: (value: any, row: any) => {
            return (
              Math.ceil(
                dayjs(row?.clientDueDate).diff(Date.now(), "day", true)
              ) || ""
            );
          },
          renderHeader: (params: any) => (
            <Typography sx={{ fontWeight: "bold" }}>ETA</Typography>
          ),
        },
      ]
      : []),
    {
      field: "address",
      headerName: "Address",
      width: 180,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      valueGetter: (value, row) => {
        return row?.propertyRecord?.street || "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="address"
          sortField="address"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "city",
      headerName: "City",
      width: 160,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      valueGetter: (value, row) => {
        return row?.propertyRecord?.city || "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="city"
          sortField="city"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "state",
      headerName: "State",
      width: 160,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      valueGetter: (value, row) => {
        return row?.propertyRecord?.state || "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="state"
          sortField="state"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "zip",
      headerName: "Zip",
      width: 160,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      valueGetter: (value, row) => {
        return row?.propertyRecord?.zip || "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="zip"
          sortField="zip"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "propertyContactName",
      headerName: "Property Contact Name",
      width: 240,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      valueGetter: (value, row) => {
        return row.propertyContacts && row.propertyContacts.length > 0
          ? row.propertyContacts[0].name
          : "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="propertyContactName"
          sortField="propertycontactname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "propertyContactPhone",
      headerName: "Property Contact Phone",
      width: 240,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      valueGetter: (value, row) => {
        return row.propertyContacts && row.propertyContacts.length > 0
          ? row.propertyContacts[0].phone
          : "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="propertyContactPhone"
          sortField="propertycontactphone"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    ...(currentUser?.userType !== userType.client
      ? [
        {
          field: "inspectorFirm",
          headerName: "Inspector",
          width: 220,
          sortable: false,
          resizable: false,
          headerClassName: "no-separator",
          valueGetter: (value: any, row: any) => {
            return row?.inspectorFirm?.displayName || "";
          },
          renderHeader: (params: any) => (
            <TableHeaderTextField
              field="inspectorFirmName"
              sortField="inspectorfirmname"
              headerName={params.colDef.headerName}
              width={params.colDef.computedWidth}
              showFilter={showTableFilter}
              onFilterSubmit={onFilterUsers}
            />
          ),
        },
      ]
      : []),
    ...(currentUser?.userType !== userType.client
      ? [
        {
          field: "daysInQueue",
          headerName: "Days In Queue",
          width: 180,
          sortable: false,
          resizable: false,
          headerClassName: "no-separator",
          renderHeader: () => (
            <Typography sx={{ fontWeight: "bold" }}>Days In Queue</Typography>
          ),
        },
      ]
      : []),
  ];

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    dispatch(getAllOrdersSuccess({ response: null }));
  }, []); // eslint-disable-line

  useEffect(() => {
    setRowData(allOrders?.items);
  }, [allOrders]);

  useEffect(() => {
    if (paginationModel) {
      const pagination = { ...paginationModel };
      pagination.page = paginationModel?.page + 1;
      setFilterParams({ ...filterParams, ...pagination });
    }
  }, [paginationModel]); // eslint-disable-line

  useEffect(() => {
    //Removing filterParams object key if their value is empty
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });
    if (Object.keys(filters).length > 0) {
      dispatch(getAllOrdersRequest(filters));
    }
  }, [filterParams]); // eslint-disable-line

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterUsers = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else if (keys.includes("clientDueDate.Operator")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          //if isActive status is false and if userType is 0 then condition also should pass
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  const handleDeleteClick = () => {
    if (rowSelectionModel.length === 0) return;
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (rowSelectionModel.length === 0) return;

    setIsProcessing(true);
    const orderIds = rowSelectionModel as string[];

    // Delete all selected orders
    const response = await deleteOrderRequest(orderIds);
    setIsProcessing(false);

    if (response?.status === 200 || response?.status === 204) {
      setSuccessMessage(
        orderIds.length === 1
          ? "Order deleted successfully."
          : `${orderIds.length} orders deleted successfully.`
      );
      setShowSuccessNotification(true);
      setRowSelectionModel([]);
      setShowDeleteDialog(false);
      // Refresh the orders list
      dispatch(getAllOrdersRequest(filterParams));
    } else {
      setErrorMessage("Failed to delete orders. Please try again.");
      setShowErrorNotification(true);
      setShowDeleteDialog(false);
    }
  };

  const updateQuickNote = async (orderId: string, note: string) => {
    const payload = {
      quickNote: note,
    };
    setShowLoading(true);
    const response = await updateQuickNoteRequest(orderId, payload);
    setShowLoading(false);
    if (response?.status === 200 || response?.status === 204) {
      setSuccessMessage("Updated Quick Note Successfully.");
      setShowSuccessNotification(true);
    } else if (response?.status === 400 || response?.status === 404) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const onClearAllFilters = () => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setFilterParams({
      queueType: queueType.created,
      page: 1,
      pageSize: 10,
    } as any);
    setFilterResetKey((prev) => prev + 1);
  };

  return (
    <>
      <Box
        className="clients-header"
        sx={{
          mt: 3,
          mb: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display={"flex"} alignItems={"center"}>
          {currentUser?.userType === userType.tenant && (
            <IconButton
              aria-label="delete"
              disabled={rowSelectionModel.length === 0 || isProcessing}
              onClick={handleDeleteClick}
            >
              <DeleteOutlinedIcon
                color={rowSelectionModel.length === 0 ? "disabled" : "error"}
              />
            </IconButton>
          )}
        </Box>
        <Box>
          <IconButton aria-label="clear-all" onClick={onClearAllFilters}>
            <ClearAllOutlined color="primary" />
          </IconButton>
          <IconButton
            aria-label="filter"
            onClick={() => onFilterClick(!showTableFilter)}
          >
            {showTableFilter ? (
              <FilterAltOffOutlinedIcon color="primary" />
            ) : (
              <FilterAltOutlinedIcon color="primary" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mx: 2 }}>
        <Table
          key={filterResetKey}
          rowData={rowData}
          rowCount={rowCount}
          loading={loading}
          columnDef={column}
          headerHeight={headerHeight}
          disableVirtualization={true}
          disableColumnMenu={true}
          disableColumnResize={false}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          checkboxSelection={true}
          onRowSelectionModelChange={(newRowSelectionModel: any) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          processRowUpdate={(updatedRow, originalRow) => {
            if (originalRow?.quickNote !== updatedRow.quickNote) {
              updateQuickNote(updatedRow.id, updatedRow.quickNote);
            }
            setRowData((prev: any) =>
              prev.map((r: any) => (r.id === updatedRow.id ? updatedRow : r))
            );
            return updatedRow;
          }}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <DeleteOrderDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        count={rowSelectionModel.length}
        isProcessing={isProcessing}
      />

      {/* Success Notification */}
      <Notification
        show={showSuccessNotification}
        onClose={() => setShowSuccessNotification(false)}
        title="Success"
        message={successMessage}
      />

      {/* Error Notification */}
      <ErrorNotification
        show={showErrorNotification}
        onClose={() => setShowErrorNotification(false)}
        title="Error"
        message={errorMessage}
      />

      {showLoading && <Loading />}
    </>
  );
}
